import React from "react";
import { useSelector } from "react-redux";

import constants from "@/constants";
import { fetchInternalFairValidation } from "@/api/events";
import FinalStaffingTab from "./finalStaffingTab";
import StatsTab from "./statsTab";
import StudentReportTab from "./studentReportTab";
import UniversityWiseTab from "./universityWiseTab";

import {
    Box,
    CircularProgress,
    Grid,
    Tab,
    Tabs,
    Typography,
    makeStyles,
    withStyles,
} from "@material-ui/core";
import NoDataPreview from "@/components/shared/noDataPreview";

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(12),
        padding: theme.spacing(0, 3, 3),
        [theme.breakpoints.down("sm")]: {
            marginTop: theme.spacing(10),
            padding: theme.spacing(0, 1, 2),
        },
    },
    titleRow: {
        marginBottom: theme.spacing(2),
    },
    tabsCard: {
        background: theme.palette.common.white,
        borderRadius: theme.spacing(1),
        overflow: "hidden",
    },
    tabPanel: {
        padding: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2, 1),
        },
    },
}));

const DetailsTabs = withStyles(() => ({
    root: {
        borderBottom: "1px solid #e8e8e8",
    },
}))(Tabs);

const DetailTab = withStyles(() => ({
    root: {
        textTransform: "none",
        minWidth: 120,
        "&:hover": {
            color: "#f36c21",
            opacity: 1,
        },
        "&$selected": {
            color: "#f36c21",
            fontWeight: "600",
        },
        "&:focus": {
            color: "#f36c21",
        },
    },
    selected: {},
}))((props) => <Tab disableRipple {...props} />);

export default function InternalFairStudents() {
    const classes = useStyles();
    const location = useSelector((state) => state.router.location);
    const search = new URLSearchParams(location.search);
    const fairId = (search.get("fair_id") || "").trim();
    const shouldShowStats = search.get("showStats") === "1";
    const shouldShowStaffing = search.get("showStaffing") === "1";

    const [fair, setFair] = React.useState(null);
    const [validationLoading, setValidationLoading] = React.useState(true);
    const [validationError, setValidationError] = React.useState("");
    const [activeTab, setActiveTab] = React.useState("student-report");
    const [totalStudents, setTotalStudents] = React.useState(0);

    const tabs = React.useMemo(() => {
        const baseTabs = [
            { value: "student-report", label: "Student Report" },
            { value: "university-wise", label: "University Wise" },
        ];

        if (shouldShowStaffing) {
            baseTabs.push({ value: "final-staffing", label: "Final Staffing" });
        }
        if (shouldShowStats) {
            baseTabs.push({ value: "stats", label: "Stats" });
        }

        return baseTabs;
    }, [shouldShowStaffing, shouldShowStats]);

    React.useEffect(() => {
        if (!shouldShowStats && activeTab === "stats") {
            setActiveTab("student-report");
        }
    }, [activeTab, shouldShowStats]);

    React.useEffect(() => {
        if (!shouldShowStaffing && activeTab === "final-staffing") {
            setActiveTab("student-report");
        }
    }, [activeTab, shouldShowStaffing]);

    React.useEffect(() => {
        if (!fairId) {
            setValidationLoading(false);
            setValidationError("Fair ID is required.");
            return;
        }

        setValidationLoading(true);
        setValidationError("");
        fetchInternalFairValidation(fairId)
            .then((res) => {
                setFair(res.data);
            })
            .catch(() => {
                setFair(null);
                setValidationError("Fair not found. Please check the fair ID.");
            })
            .finally(() => setValidationLoading(false));
    }, [fairId]);

    if (validationLoading) {
        return (
            <Box className={classes.root} textAlign="center">
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (validationError) {
        return (
            <Box className={classes.root}>
                <NoDataPreview title={validationError} />
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            <Grid container justifyContent="space-between" alignItems="center" className={classes.titleRow} spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h2">
                        {fair && fair.fair_name
                            ? `${fair.fair_name}${totalStudents ? ` (${totalStudents})` : ""}`
                            : "Internal Fair Students"}
                    </Typography>
                    <Typography variant="subtitle1">Fair ID: {fairId}</Typography>
                </Grid>
            </Grid>

            <Box className={classes.tabsCard}>
                <DetailsTabs
                    value={activeTab}
                    onChange={(_, nextTab) => setActiveTab(nextTab)}
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {tabs.map((tab) => (
                        <DetailTab key={tab.value} value={tab.value} label={tab.label} />
                    ))}
                </DetailsTabs>

                {activeTab === "student-report" ? (
                    <Box className={classes.tabPanel} role="tabpanel">
                        <StudentReportTab
                            fairId={fairId}
                            onTotalStudentsChange={setTotalStudents}
                            jsonUrl={constants.internalFairUniversitiesJsonUrl}
                        />
                    </Box>
                ) : null}

                {activeTab === "university-wise" ? (
                    <Box className={classes.tabPanel} role="tabpanel">
                        <UniversityWiseTab fairId={fairId} jsonUrl={constants.internalFairUniversitiesJsonUrl} />
                    </Box>
                ) : null}

                {shouldShowStaffing && activeTab === "final-staffing" ? (
                    <Box className={classes.tabPanel} role="tabpanel">
                        <FinalStaffingTab jsonUrl={constants.internalFairUniversitiesJsonUrl} />
                    </Box>
                ) : null}

                {shouldShowStats && activeTab === "stats" ? (
                    <Box className={classes.tabPanel} role="tabpanel">
                        <StatsTab fairId={fairId} />
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
